import React, { ReactNode } from "react";
import { pluralize } from "@common/helpers/string";
import { usePersonalCategory } from "@hooks/usePersonalCategory";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { BulkActionsToastData } from "@components/transactions/TransactionsBulkActions/lib/bulkActions";

import { Button } from "@design-system/Button/Button";
import { NameForValueToAssign } from "../../../counterparty/SimilarTransactionsUpdateToast/SimilarTransactionsUpdateToast";
import { shouldShowGoingForwardMessage } from "./ToastSuccess.lib";

type Props = BulkActionsToastData;

interface AdditionalMessageOptions {
    shouldShow: boolean;
    isRemoval: boolean;
    removalMessage: ReactNode;
    assignMessage: ReactNode;
}

function getAdditionalMessage({
    shouldShow,
    isRemoval,
    removalMessage,
    assignMessage,
}: AdditionalMessageOptions): ReactNode | undefined {
    if (!shouldShow) {
        return undefined;
    }
    return isRemoval ? removalMessage : assignMessage;
}

export const ToastSuccess: React.FC<Props> = ({
    updateDetails,
    displayedChangedEntityName,
    previousTransactions,
    undo,
    hideGoingForwardMessage,
}) => {
    const personalCategory = usePersonalCategory();
    const { enableDescriptionNormalization } = useTypedFlags();

    const displayedName = (
        <NameForValueToAssign
            valueToAssign={updateDetails}
            displayedChangedEntityName={displayedChangedEntityName}
            useBold
        />
    );

    let message: ReactNode | undefined;

    const numberOfTransactionsSpan = (
        <b>
            {updateDetails.transactionsCount}{" "}
            {pluralize("transaction", updateDetails.transactionsCount)}
        </b>
    );

    const shouldShowAdditionalMessage = shouldShowGoingForwardMessage({
        enableDescriptionNormalization,
        hideGoingForwardMessage,
        update: updateDetails.update,
    });

    if (updateDetails.isUndo) {
        message = <span>Changes reverted for {numberOfTransactionsSpan}.</span>;
    } else {
        switch (updateDetails.update) {
            case "categoryId":
                if (updateDetails.value === personalCategory.id) {
                    message = (
                        <span>
                            {numberOfTransactionsSpan} marked as personal
                        </span>
                    );
                } else {
                    const additionalMessage = shouldShowAdditionalMessage ? (
                        <span>
                            Kick will categorize similar transactions as{" "}
                            {displayedName} going forward.
                        </span>
                    ) : (
                        ""
                    );
                    message = (
                        <span>
                            Category updated to {displayedName} for{" "}
                            {numberOfTransactionsSpan}. {additionalMessage}
                        </span>
                    );
                }
                break;

            case "counterpartyId": {
                const additionalMessage = getAdditionalMessage({
                    shouldShow: shouldShowAdditionalMessage,
                    isRemoval: updateDetails.value === null,
                    removalMessage: (
                        <span>
                            Kick won't assign a counterparty to similar
                            transactions going forward.
                        </span>
                    ),
                    assignMessage: (
                        <span>
                            Kick will assign {displayedName} to similar
                            transactions going forward.
                        </span>
                    ),
                });
                message =
                    updateDetails.value === null ? (
                        <span>
                            Counterparty removed for {numberOfTransactionsSpan}.{" "}
                            {additionalMessage}
                        </span>
                    ) : (
                        <span>
                            Counterparty updated to {displayedName} for{" "}
                            {numberOfTransactionsSpan}. {additionalMessage}
                        </span>
                    );
                break;
            }

            case "classes": {
                const numberOfClasses = updateDetails.value.length;
                const additionalMessage = getAdditionalMessage({
                    shouldShow: shouldShowAdditionalMessage,
                    isRemoval: numberOfClasses === 0,
                    removalMessage: (
                        <span>
                            Kick won't assign classes to similar transactions
                            going forward.
                        </span>
                    ),
                    assignMessage: (
                        <span>
                            Kick will assign {displayedName} to similar
                            transactions going forward.
                        </span>
                    ),
                });
                message =
                    numberOfClasses === 0 ? (
                        <span>
                            Classes removed for {numberOfTransactionsSpan}.{" "}
                            {additionalMessage}
                        </span>
                    ) : (
                        <span>
                            {displayedName} assigned to{" "}
                            {numberOfTransactionsSpan}. {additionalMessage}
                        </span>
                    );
                break;
            }

            case "coa": {
                const additionalMessage = getAdditionalMessage({
                    shouldShow: shouldShowAdditionalMessage,
                    isRemoval: updateDetails.value === null,
                    removalMessage: (
                        <span>
                            Kick won't assign a Cash ledger to similar
                            transactions going forward.
                        </span>
                    ),
                    assignMessage: (
                        <span>
                            Kick will categorize similar transactions as{" "}
                            {displayedName} going forward.
                        </span>
                    ),
                });
                message =
                    updateDetails.value === null ? (
                        <span>
                            Cash ledger reverted for {numberOfTransactionsSpan}.{" "}
                            {additionalMessage}
                        </span>
                    ) : (
                        <span>
                            Cash ledger updated to <b>{displayedName}</b> for{" "}
                            {numberOfTransactionsSpan}. {additionalMessage}
                        </span>
                    );
                break;
            }

            case "accrualCoa": {
                const additionalMessage = getAdditionalMessage({
                    shouldShow: shouldShowAdditionalMessage,
                    isRemoval: updateDetails.value === null,
                    removalMessage: (
                        <span>
                            Kick won't assign an accrual ledger to similar
                            transactions going forward.
                        </span>
                    ),
                    assignMessage: (
                        <span>
                            Kick will categorize similar transactions as{" "}
                            {displayedName} going forward.
                        </span>
                    ),
                });
                message =
                    updateDetails.value === null ? (
                        <span>
                            Accrual ledger reverted for{" "}
                            {numberOfTransactionsSpan}. {additionalMessage}
                        </span>
                    ) : (
                        <span>
                            Accrual ledger updated to <b>{displayedName}</b> for{" "}
                            {numberOfTransactionsSpan}. {additionalMessage}
                        </span>
                    );
                break;
            }

            case "taxQuestionAnswers":
                message = (
                    <span>Answer saved for {numberOfTransactionsSpan}.</span>
                );
                break;

            case "memo":
                message = (
                    <span>Memo updated for {numberOfTransactionsSpan}.</span>
                );
                break;

            case "entity":
                message = (
                    <span>
                        {numberOfTransactionsSpan} moved to{" "}
                        <b>{displayedName}</b>.
                    </span>
                );
                break;

            default:
                message = <span>{numberOfTransactionsSpan} updated.</span>;
                break;
        }
    }

    return (
        <>
            <div className="transactions-update-toast__content">
                <p className="transactions-update-toast__label">{message}</p>
            </div>

            {previousTransactions && undo && (
                <div className="transactions-update-toast__actions">
                    <Button
                        variant="primary"
                        size="md"
                        onClick={() =>
                            undo({
                                previousTransactions,
                                updateDetailsForUndo: updateDetails,
                            })
                        }
                    >
                        Undo
                    </Button>
                </div>
            )}
        </>
    );
};
